import { MedicalDataTypes, APITypes, AuditLogs } from '../../../primary-record-types';
import { useAuditLogs } from '../../../hooks/api/useAuditLogs';
import useMedicalProfileData from '../../../hooks/api/useMedicalProfileData';
import { useMemo } from 'react';
import { AuditLogEventType, EventType, nonAllEventTypes } from './types';
import { MonthYear } from '../Calendar';
import { colors } from '../../../utils/theme';
import { useHasPermission, useIsLoadingPermissions } from '../Permissions/hooks';
import { categoryToDomain } from '../../../utils/resourceUtils/medicalDataPermissions';

const eventAuditLogTypeAuditLogMap: Record<AuditLogEventType, AuditLogs.AuditLogTypes[]> = {
    recordUpdates: [
        'resource-creation',
        'resource-update',
        'resource-delete',
        'medical-profile-file-upload',
        'medical-profile-file-delete',
        'code-status-directive-creation',
        'code-status-directive-update',
        'poa-directive-creation',
        'poa-directive-update',
        'no-known-allergies-indicated',
    ],
    collaboration: [
        'collaborator-add',
        'collaborator-remove',
        'collaborator-invite',
        'collaborator-invite-accept',
        'collaborator-invite-decline',
        'care-team-member-collaborator-remove',
        'care-team-member-collaborator-add',
        'note-creation',
        'note-move',
        'note-comment',
        'shared-report-creation',
        'shared-report-viewed',
        'profile-message',
        'organization-collaborator-add',
        'organization-collaborator-remove',
        'organization-invoice-creation',
    ],
    profile: [
        'user-profile-view',
        '1up-integration-add',
        '1up-integration-refresh',
        '1up-integration-remove',
        'fasten-integration-add',
        'fasten-integration-refresh',
        'fasten-integration-remove',
        'portal-refresh-completion',
        'medical-profile-ownership-transfer',
    ],
};

const getEventTypeFromAuditLogType = (auditLogType: AuditLogs.AuditLogTypes): AuditLogEventType | null => {
    return (
        (Object.keys(eventAuditLogTypeAuditLogMap) as AuditLogEventType[]).find((eventType) =>
            eventAuditLogTypeAuditLogMap[eventType].includes(auditLogType),
        ) || null
    );
};

export const eventTypeColorMap: Record<EventType, string> = {
    profile: colors['Event-Filter-Profile'],
    collaboration: colors['Event-Filter-Collaboration'],
    recordUpdates: colors['Event-Filter-Record-Update'],
    visits: colors['Event-Filter-Visit'],
    all: colors['Event-Filter-All'],
};

export const getColorForAuditLogType = (auditLogType: AuditLogs.AuditLogTypes): string => {
    const eventType = getEventTypeFromAuditLogType(auditLogType);
    if (!eventType) return eventTypeColorMap.all;

    return eventTypeColorMap[eventType];
};

export const allCalendarAuditLogTypes = Object.values(eventAuditLogTypeAuditLogMap).flat();

export type UseCalendarEventsArgs = {
    medicalProfileId: string;
};

export type UseCalendarEventsReturnValue = {
    isLoading: boolean;
    calendarEventsHelper: CalendarEventHelper;
};

type DayEvents = {
    recordUpdates: APITypes.auditLogService.AuditLogListItem[];
    collaboration: APITypes.auditLogService.AuditLogListItem[];
    profile: APITypes.auditLogService.AuditLogListItem[];
    visits: APITypes.medicalDataService.ListResourceResponseItem<MedicalDataTypes.PrimaryRecordVisit>[];
};

export type AuditLogEvent = {
    type: 'audit-log';
    auditLog: APITypes.auditLogService.AuditLogListItem;
    dateTimeIso: string;
};

export type VisitEvent = {
    type: 'visit';
    visit: APITypes.medicalDataService.ListResourceResponseItem<MedicalDataTypes.PrimaryRecordVisit>;
    dateTimeIso: string;
};

export type Event = AuditLogEvent | VisitEvent;

const getDefaultDayEvent = (): DayEvents => ({
    profile: [],
    collaboration: [],
    recordUpdates: [],
    visits: [],
});

type DayEventsHash = Record<string, DayEvents>;

const getKeyFromDateTime = (dateTime: string): string => {
    return dateTime.split('T')[0];
};

export class CalendarEventHelper {
    private _dayEvents: DayEventsHash = {};
    private _sortedEvents: Event[] = [];

    constructor(
        auditLogs: APITypes.auditLogService.AuditLogListItem[],
        visits: APITypes.medicalDataService.ListResourceResponseItem<MedicalDataTypes.PrimaryRecordVisit>[],
    ) {
        auditLogs.forEach((auditLog) => {
            const key = getKeyFromDateTime(new Date(auditLog.timestamp).toISOString());
            if (!this._dayEvents[key]) {
                this._dayEvents[key] = getDefaultDayEvent();
            }

            const auditLogEventType = getEventTypeFromAuditLogType(auditLog.type);
            if (auditLogEventType) {
                this._dayEvents[key][auditLogEventType].push(auditLog);
            }
        });

        const visitsWithDates = visits.filter((visit) => visit.startDateTime);

        visitsWithDates.forEach((visit) => {
            if (!visit.startDateTime) return;

            const key = getKeyFromDateTime(visit.startDateTime);
            if (!this._dayEvents[key]) {
                this._dayEvents[key] = getDefaultDayEvent();
            }
            this._dayEvents[key].visits.push(visit);
        });

        const unsortedEvents: Event[] = [];
        auditLogs.forEach((auditLog) => {
            unsortedEvents.push({
                type: 'audit-log',
                auditLog,
                dateTimeIso: new Date(auditLog.timestamp).toISOString(),
            });
        });

        visitsWithDates.forEach((visit) => {
            if (!visit.startDateTime) return;

            unsortedEvents.push({
                type: 'visit',
                visit,
                dateTimeIso: visit.startDateTime,
            });
        });

        this._sortedEvents = unsortedEvents.sort((a, b) => {
            return new Date(a.dateTimeIso).getTime() - new Date(b.dateTimeIso).getTime();
        });
    }

    public getHasEvents = (dateTime: string, eventType: EventType): boolean => {
        const events = this._dayEvents[getKeyFromDateTime(dateTime)];
        if (
            !(
                events?.collaboration.length ||
                events?.profile.length ||
                events?.recordUpdates.length ||
                events?.visits.length
            )
        )
            return false;

        if (eventType === 'all') return true;

        return events[eventType].length > 0;
    };

    public getEvents = (dateTime: string): DayEvents => {
        return this._dayEvents[getKeyFromDateTime(dateTime)] || getDefaultDayEvent();
    };

    public getEventTypesForDay = (dateTime: string, eventTypeFilter: EventType): EventType[] => {
        const eventsForDay = this.getEvents(dateTime);

        const filteringEventTypes: EventType[] = eventTypeFilter === 'all' ? nonAllEventTypes : [eventTypeFilter];

        const filtered: DayEvents = {
            profile: filteringEventTypes.includes('profile') ? eventsForDay.profile : [],
            collaboration: filteringEventTypes.includes('collaboration') ? eventsForDay.collaboration : [],
            recordUpdates: filteringEventTypes.includes('recordUpdates') ? eventsForDay['recordUpdates'] : [],
            visits: filteringEventTypes.includes('visits') ? eventsForDay.visits : [],
        };

        const returnValue: EventType[] = [];
        if (filtered.profile.length) returnValue.push('profile');
        if (filtered.collaboration.length) returnValue.push('collaboration');
        if (filtered.recordUpdates.length) returnValue.push('recordUpdates');
        if (filtered.visits.length) returnValue.push('visits');

        return returnValue;
    };

    public getMonthYearEvents = (monthYear: MonthYear, eventType: EventType): Event[] => {
        return this._sortedEvents
            .filter((event) => {
                const dateTime = new Date(event.dateTimeIso);
                return dateTime.getFullYear() === monthYear.year && dateTime.getMonth() + 1 === monthYear.month;
            })
            .filter((event) => {
                if (eventType === 'all') return true;

                if (eventType === 'visits') return event.type === 'visit';

                return (
                    event.type === 'audit-log' && eventAuditLogTypeAuditLogMap[eventType].includes(event.auditLog.type)
                );
            });
    };

    public getHasMonthYearEvents = (monthYear: MonthYear, eventType: EventType): boolean => {
        return this.getMonthYearEvents(monthYear, eventType).length > 0;
    };

    public getMonthYearEventTypes = (monthYear: MonthYear, eventTypeFilter: EventType): EventType[] => {
        const eventTypes: EventType[] = [];
        this.getMonthYearEvents(monthYear, eventTypeFilter).forEach((event) => {
            if (event.type === 'visit') {
                eventTypes.push('visits');
            } else {
                const eventType = getEventTypeFromAuditLogType(event.auditLog.type);
                if (eventType) eventTypes.push(eventType);
            }
        });
        return [...new Set(eventTypes)];
    };
}

export const useCalendarEvents = (args: UseCalendarEventsArgs): UseCalendarEventsReturnValue => {
    const { isLoading, auditLogs } = useAuditLogs({
        medicalProfileIds: [args.medicalProfileId],
        auditLogTypes: allCalendarAuditLogTypes,
    });
    // A collaborator can hold access to the profile while visits are denied, so the category is only
    // requested when the viewer may see it. Leaving resourceCategory undefined disables the query --
    // useMedicalProfileData already gates on it -- rather than issuing a 403 this screen cannot act on.
    const isLoadingPermissions = useIsLoadingPermissions(args.medicalProfileId);
    const canViewVisits = useHasPermission(args.medicalProfileId, categoryToDomain('visit'), 'view');

    const { isLoadingResources, resources } = useMedicalProfileData({
        medicalProfileId: args.medicalProfileId,
        resourceCategory: canViewVisits ? 'visit' : undefined,
    });

    const calendarEventHelper = useMemo(
        () =>
            new CalendarEventHelper(
                auditLogs,
                (resources.resources ||
                    []) as APITypes.medicalDataService.ListResourceResponseItem<MedicalDataTypes.PrimaryRecordVisit>[],
            ),
        [auditLogs, resources.resources],
    );

    return {
        // Permissions are part of the loading state on purpose: until they resolve canViewVisits is false,
        // so a permitted viewer would otherwise see the calendar settle without visits and then have them
        // appear a moment later.
        isLoading: isLoading || isLoadingResources || isLoadingPermissions,
        calendarEventsHelper: calendarEventHelper,
    };
};
